import Portal from "@/managers/portal"
import Tracking from "@/managers/session/logging/tracking"
import AuthenticationMethodBase from "@/managers/authentication/authenticationMethodBase"
import { browserSupportsWebAuthn, browserSupportsWebAuthnAutofill, startAuthentication, startRegistration, WebAuthnError } from "@simplewebauthn/browser"
import Vue from "vue"
import {watch} from "@/store/store"
import Notifications from "@/managers/session/notifications"

class Passkey extends AuthenticationMethodBase {
	private readonly passkeyState = Vue.observable({isRegistering: false, isRegistered: false, isAutofillSupported: null as boolean | null, isWebAuthSupported: browserSupportsWebAuthn()})

	public get isRegistering(): boolean {
		return this.passkeyState.isRegistering
	}

	public get isRegistered(): boolean {
		return this.passkeyState.isRegistered
	}

	public get canAutofill(): boolean | null {
		return this.passkeyState.isAutofillSupported
	}

	public get canRegister(): boolean {
		return this.passkeyState.isWebAuthSupported
	}

	public get isOriginPasskey(): boolean {
		return Portal.hasAccessToken && Portal.accessToken!.isOriginPasskey
	}

	public get isPasskeyAuthenticated(): boolean {
		return Portal.hasAccessToken && Portal.accessToken!.isPasskeyAuthenticated
	}

	constructor() {
		super(browserSupportsWebAuthn())

		browserSupportsWebAuthnAutofill()
			.then(supported => this.passkeyState.isAutofillSupported = supported)

		watch(() => Portal.accessToken, token => {
			if (token !== null) {
				if (!token.isOriginPasskey)
					this.passkeyState.isRegistered = true
			} else
				this.passkeyState.isRegistered = false
		}, true)
	}

	public async login(): Promise<void> {
		return this.innerLogin(false)
	}

	public async loginWithAutofill(): Promise<void> {
		try {
			await this.innerLogin(true)
		} catch (error: any) {
			Notifications.warning("Passkey autofill failed", error)
		}
	}

	public async register(name: string, ticket: string | null = null): Promise<void> {
		if (this.isRegistering)
			throw new Error("Already registering")
		this.passkeyState.isRegistering = true

		Tracking.event("Authentication", "Register passkey")

		const originalCanLogin = this.canLogin
		this.setCanLogin(false)

		try {
			const options = await Portal.call.passkeys.CredentialOptionsPost(ticket).response

			const assertion = await startRegistration({optionsJSON: options.Options})

			await Portal.call.passkeys.CredentialsPost(name, options.OptionsId, assertion).response

			this.passkeyState.isRegistered = true
		} finally {
			this.passkeyState.isRegistering = false
			this.setCanLogin(originalCanLogin)
		}
	}

	public async requestRegister(): Promise<void> {
		if (!this.canRegister)
			throw new Error("Can't register now")

		Tracking.event("Authentication", "Requesting to register passkey")

		await Portal.call.passkeys.CredentialCreateRequestsPost().response
	}

	private async innerLogin(autofill: boolean): Promise<void> {
		if (!this.canLogin)
			throw new Error("Can't login now")

		if (!autofill)
			this.setLoggingIn()

		Tracking.event("Authentication", "Logging in with passkey")

		try {
			const options = await Portal.call.passkeys.AssertionOptionsPost().response

			const assertion = await startAuthentication({optionsJSON: options.Options, useBrowserAutofill: autofill})

			await Portal.call.passkeys.AssertionPost(options.OptionsId, assertion as any).response

			this.passkeyState.isRegistered = true
		} finally {
			if (!autofill)
				this.setCanLogin(true)
		}
	}
}

export {WebAuthnError}

export default new Passkey()
